/**
 * Reading survival data from a pasted table: time, status, and an optional group.
 *
 * The status column holds two labels (1/0, Dead/Alive, Event/Censored); the reader says
 * which one means "the event happened". Rows with a missing cell are set aside, and a
 * time that is not a non-negative number is reported with its line.
 */

import { parseNumberInput } from "@/lib/parse-number";
import type { Observation } from "@/lib/tool-kit/stats-clinical/kaplan-meier";

export interface SurvivalRows {
  times: number[];
  statuses: string[];
  groups: string[];
  dropped: number;
  hasGroups: boolean;
  /** The first time cell that was not a non-negative number. */
  unreadable: { line: number; text: string } | null;
}

export function readSurvivalRows(
  rows: readonly (readonly string[])[],
  lines: readonly number[]
): SurvivalRows {
  const result: SurvivalRows = {
    times: [],
    statuses: [],
    groups: [],
    dropped: 0,
    hasGroups: rows.some((row) => row.length >= 3),
    unreadable: null,
  };
  for (const [index, row] of rows.entries()) {
    const [timeCell = "", statusCell = "", groupCell = ""] = row;
    if (
      timeCell === "" ||
      statusCell === "" ||
      (result.hasGroups && groupCell === "")
    ) {
      result.dropped += 1;
      continue;
    }
    const time = parseNumberInput(timeCell);
    if (time === null || time < 0) {
      result.unreadable ??= { line: lines[index] ?? index + 1, text: timeCell };
      continue;
    }
    result.times.push(time);
    result.statuses.push(statusCell);
    result.groups.push(result.hasGroups ? groupCell : "");
  }
  return result;
}

const EVENT_WORDS = new Set([
  "1",
  "true",
  "yes",
  "y",
  "event",
  "dead",
  "death",
  "died",
  "deceased",
  "relapse",
  "relapsed",
  "failure",
  "progression",
  "progressed",
]);

/** The status label most likely to mean "the event happened". */
export function guessEventLabel(labels: readonly string[]): string {
  // R's convention for 1/2 coding: 2 is the event.
  if (labels.length === 2 && labels.includes("1") && labels.includes("2")) {
    return "2";
  }
  const match = labels.find((label) => EVENT_WORDS.has(label.toLowerCase()));
  if (match !== undefined) {
    return match;
  }
  const sorted = [...labels].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true })
  );
  return sorted.at(-1) ?? "";
}

export interface GroupedObservations {
  names: string[];
  groups: Observation[][];
}

/** Observations split by group, groups in natural sort order. */
export function groupObservations(
  rows: SurvivalRows,
  eventLabel: string
): GroupedObservations {
  const names = [...new Set(rows.groups)].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true })
  );
  const groups: Observation[][] = names.map(() => []);
  for (const [index, time] of rows.times.entries()) {
    const position = names.indexOf(rows.groups[index]);
    groups[position].push({ time, event: rows.statuses[index] === eventLabel });
  }
  return { names, groups };
}
